import { resolvePortalMessage } from "../i18n/message.js";
import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The app shell — the frame every authenticated screen is drawn inside.
 *
 * Three sidebar groups, the workspace switcher, the notification centre, the person menu,
 * the billing alert banner and the theme toggle, per P10-01. The shell owns the chrome and
 * nothing else: it renders `<Outlet />` and has no idea which screen is inside it.
 *
 * AMENDED 2026-09-25 (customer-portal review #53, #88, #118, #133, #134, #135, #163):
 *
 *   · what the chrome offers follows the signed-in role (`member-capability.ts`): the sidebar
 *     leaves out pages the role cannot read, their counts are not even requested (a Viewer's
 *     every page load used to answer two 403s), and "New agent" is withheld, with the reason, from
 *     a role that cannot create one;
 *   · the menu button works: on a wide screen it folds the sidebar away, below tablet width it
 *     opens the sidebar as an overlay (which closes when a row is chosen);
 *   · the billing banner's "Add credit" opens Billing instead of doing nothing.
 *
 * AMENDED 2026-10-05 (plan-014 A4 — FR12-AUTH-09…12, FR12-SCR-05; design §27.6, §27.15.1 "App shell"):
 *
 *   · the shell draws the auth_session in each of its THREE contexts (`authContextQuery`):
 *       selection_required  the organization chooser, and nothing else;
 *       account_only        the organization picker, "Finance only · no workspace" (or "No workspace
 *                           open"), and only the Organization group — no workspace picker, no
 *                           operational navigation, no workspace counts, credit or notifications;
 *       workspace_selected  the organization picker AND the workspace picker (enterable workspaces
 *                           only), every group the roles allow;
 *     so the selected organization and workspace are always visible;
 *   · everything is inside the scope guard (`../scope/guard.tsx`): switching sends the scope
 *     generation, tells the other tabs, asks first when a tab holds unsaved edits, cancels and clears
 *     every query, closes every stream, drops scope-bound drafts and shows the post-switch notice;
 *   · a page the context may not show (a deep link to a workspace page with the organization alone
 *     open, an Organization page without an organization role) is never drawn: the shell sends the
 *     person to the context's landing instead (FR12-AUTH-10 "deep links always recheck access");
 *   · the next allowed destination (FR12-AUTH-12): a workspace that was archived moves the person to
 *     their next enterable workspace (or the organization alone), and a suspended organization to its
 *     Finance pages for someone who can settle it, each with a notice saying so.
 *
 * AMENDED 2026-10-06 (customer-portal review #150): the signed-in workspace's name comes from the
 * workspace listing (`current-workspace.ts`), not the sign-in, so a listing that fails no longer
 * takes the shell down: the sidebar card and the switcher say it could not be read, with Try again.
 *
 * AMENDED 2026-10-09 (design sync, Claude Design version 1791495439483263): the top bar's "New agent"
 * button is gone (agents are created from Agents' own "Create new agent"), the separator before the
 * page title is "/" like the one before the workspace, the sidebar's workspace card is gone (the
 * workspace picker's "Manage workspaces" opens Organization → Workspaces), and the page ground is
 * `--wt-page`.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Button, IconButton } from "@wetalk/ui";
import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";

import { PortalLanguagePicker } from "../i18n/language-control.js";
import { BillingAlert } from "./billing-alert.js";
import { ReadyBanner } from "./ready-banner.js";
import { useCurrentWorkspace } from "./current-workspace.js";
import { ImpersonationBanner } from "./impersonation-banner.js";
import { GLYPH } from "./glyph.js";
import { memberCan } from "./member-capability.js";
import type { NavigationScope } from "./navigation.js";
import { NotificationCentre } from "./notification-centre.js";
import { OrganizationChooser, OrganizationPicker } from "./organization-picker.js";
import { PersonMenu } from "./person-menu.js";
import { PostSwitchNotice } from "./post-switch-notice.js";
import { DomainJoinOfferBanner } from "./domain-join-offer.js";
import { ClosedOrganizationMenu, FormerOwnerClosure } from "./former-owner-closure.js";
import { Brand, Sidebar } from "./sidebar.js";
import { WorkspacePicker } from "./workspace-picker.js";
import { useGateway } from "../api/provider.js";
import {
  QUERY_KEY,
  authContextQuery,
  billingQuery,
  contactListQuery,
  notificationQuery,
  voiceAgentListQuery,
  workspaceQuery,
} from "../api/query.js";
import type {
  AccountChoiceView,
  AccountOnlyAuthSessionView,
  AuthSessionView,
  SelectionRequiredAuthSessionView,
  WorkspaceAuthSessionView,
} from "../api/gateway.js";
import { ScopeGuard, switchProblemText, useClosedOrganizationNotice, useScopeNotice, useSwitchScope, type ScopeNotice } from "../scope/guard.js";
import { lastUsedWorkspaceOf } from "../scope/last-used.js";
import {
  currentChoiceOf,
  defaultScopeTargetOf,
  enterableWorkspaceOf,
  landingPathOf,
  pathAllowedIn,
} from "../scope/target.js";
import type { NavigationCount } from "./sidebar.js";

/**
 * One badge's figure from its query: a number once it has arrived, `null` (a same-width
 * placeholder) while it is loading, and absent when it failed or was never asked for (review #53).
 */
function figureOf<T>(
  query: UseQueryResult<T>,
  asked: boolean,
  pick: (data: T) => number | null,
): number | null | undefined {
  if (!asked || query.isError) return undefined;
  if (query.data === undefined) return null;
  const picked = pick(query.data);
  return picked === null ? undefined : picked;
}

/** Below tablet width — the same breakpoint `shell.css` collapses the sidebar at. */
const NARROW_SCREEN = "(width < 60em)";

function subscribeToNarrow(onChange: () => void): () => void {
  const query = globalThis.matchMedia?.(NARROW_SCREEN);
  if (query === undefined) return () => undefined;
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}

function isNarrow(): boolean {
  return globalThis.matchMedia?.(NARROW_SCREEN).matches === true;
}

export interface ShellProps {
  readonly consoleVersion: string;
}

export function Shell({ consoleVersion }: ShellProps): ReactNode {
  usePortalTranslation("shell");
  return (
    <ScopeGuard>
      <ShellFrame consoleVersion={consoleVersion} />
    </ScopeGuard>
  );
}

function ShellFrame({ consoleVersion }: ShellProps): ReactNode {
  usePortalTranslation("shell");
  const gateway = useGateway();
  const current = useQuery(authContextQuery(gateway));
  useMembershipRevokedNotice(current.data);
  if (current.data === undefined) {
    return <p className="c-note">{portalText("shell", "loading.workspace")}</p>;
  }
  if (current.data === null) {
    // The route guard redirects; this is what renders for the frame in between.
    return <p className="c-note">{portalText("shell", "auth.signedOut")}</p>;
  }
  if (current.data.kind === "selection_required") {
    return <SelectionFrame current={current.data} fetchedAt={current.dataUpdatedAt} />;
  }
  return <ScopedFrame consoleVersion={consoleVersion} current={current.data} fetchedAt={current.dataUpdatedAt} />;
}

/**
 * plan-014 FIX2: the read of the auth_session that moved it out of a workspace or organization the person was removed
 * from carries `notice: membership_revoked`; the page it lands on says so, once per scope generation (the next read
 * carries no notice, and a re-render of the same answer must not show it again after it was dismissed).
 */
function useMembershipRevokedNotice(current: AuthSessionView | null | undefined): void {
  const { show } = useScopeNotice();
  // 2026-10-15: an organization closed from this tab says so instead ("… is closed. Access has ended for everyone …").
  const { revokedMoveNotice } = useClosedOrganizationNotice();
  const shownFor = useRef<number | null>(null);
  const revoked = current !== null && current !== undefined && current.notice === "membership_revoked";
  const generation = current === null || current === undefined ? null : current.scopeGeneration;
  const onPicker = current !== null && current !== undefined && current.kind === "selection_required";
  useEffect(() => {
    if (!revoked || generation === null || shownFor.current === generation) return;
    shownFor.current = generation;
    show(revokedMoveNotice(onPicker));
  }, [revoked, generation, onPicker, show, revokedMoveNotice]);
}

/** Log-out ends the auth_session on the server FIRST, then drops the cache and opens Log in. */
function useLogOut(): () => void {
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Navigating away from a session that is still live would tell the person they are signed out
  // when they are not, so the cache is dropped only once the server has ended it.
  const logOut = useMutation({
    mutationFn: () => gateway.logOut(),
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: "/log-in" });
    },
  });
  return () => {
    logOut.mutate();
  };
}

/** The switch target for picking `choice`: the last-used workspace there if still enterable, and so on. */
function targetOfPick(personId: string, choice: AccountChoiceView): ReturnType<typeof defaultScopeTargetOf> {
  return defaultScopeTargetOf(choice, lastUsedWorkspaceOf(personId, choice.accountId));
}

/** The one line a failed or waiting switch draws, wherever the switch was asked for. */
function SwitchStatus(): ReactNode {
  usePortalTranslation("shell");
  const scope = useSwitchScope();
  if (scope.pending !== null) {
    const { dirtyTab, dirtyHere } = scope.pending;
    const warning = dirtyHere && dirtyTab > 0
      ? portalText("shell", "switch.thisAndAnother")
      : dirtyHere ? portalText("shell", "switch.this")
        : dirtyTab === 1 ? portalText("shell", "switch.another")
          : portalText("shell", "switch.others", { count: dirtyTab });
    return (
      <div className="c-note c-switch-problem c-switch-confirm" role="alertdialog" aria-label={portalText("shell", "switch.unsaved")}>
        <p className="c-switch-confirm-text">
          {warning}
        </p>
        <span className="c-switch-confirm-action">
          <Button variant="primary" onClick={scope.confirm}>
            {portalText("shell", "switch.anyway")}
          </Button>
          <Button variant="ghost" onClick={scope.cancel}>
            {portalText("shell", "switch.stay")}
          </Button>
        </span>
      </div>
    );
  }
  if (scope.problem === null) return null;
  return (
    <p className="c-note c-note-critical c-switch-problem" role="alert">
      {portalText("shell", "switch.failed")} {switchProblemText(scope.problem)}
    </p>
  );
}

/** Signed in with no organization chosen: the chooser, and the person's own menu. */
function SelectionFrame({
  current,
  fetchedAt,
}: {
  readonly current: SelectionRequiredAuthSessionView;
  readonly fetchedAt: number;
}): ReactNode {
  usePortalTranslation("shell");
  const scope = useSwitchScope();
  const { revokedMoveNotice } = useClosedOrganizationNotice();
  const logOut = useLogOut();
  const personId = current.person.personId;
  const [only, ...rest] = current.accountChoice;
  // Design §27.6.3: exactly one organization to choose is opened after this fresh access read, once.
  const autoOpened = useRef<number | null>(null);
  useEffect(() => {
    if (only === undefined || rest.length > 0 || autoOpened.current === current.scopeGeneration) return;
    const target = targetOfPick(personId, only);
    if (target === null) return;
    autoOpened.current = current.scopeGeneration;
    // plan-014 FIX2: a move to the picker after a removed membership that then opens the one organization left is
    // still "taken to the next one you can open" — the switch would otherwise replace that notice with none.
    scope.switchTo(target, current.notice === "membership_revoked" ? revokedMoveNotice(false) : undefined);
  }, [only, rest.length, personId, current.scopeGeneration, current.notice, scope, revokedMoveNotice]);

  return (
    <div className="c-shell c-shell-bare">
      <ImpersonationBanner actor={current.actor} subjectDisplayName={current.person.displayName} nowMs={fetchedAt} />
      <div className="c-main">
        <header className="c-topbar">
          <span className="c-brand">
            <Brand />
          </span>
          <div className="c-topbar-actions">
            <PortalLanguagePicker />
            <PersonMenu
              person={current.person}
              billing={undefined}
              workspaceCount={null}
              onLogOut={logOut}
              workspaceOpen={false}
              personalSecurityOpen={current.actor.kind === "self"}
            />
          </div>
        </header>
        <SwitchStatus />
        <PostSwitchNotice />
        {/* plan-014 review I-14 (owner answer 47): a person with no organization yet — a new sign-up on a verified,
            auto-join domain — is offered to join here, before they create a second organization (and its trial). */}
        <DomainJoinOfferBanner personId={personId} enabled={current.actor.kind === "self"} />
        <OrganizationChooser
          choice={current.accountChoice}
          pending={scope.busy}
          problem={null}
          onPick={(choice) => {
            const target = targetOfPick(personId, choice);
            if (target !== null) scope.switchTo(target);
          }}
        />
        {/* plan-014 INT (B4's hand-off): a former Owner — often with no other organization — undoes a closure here. */}
        <FormerOwnerClosure enabled={current.actor.kind === "self"} />
      </div>
    </div>
  );
}

/** The organization a context is in, as its picker shows it — from the fresh listing, or the context itself. */
function currentAccountChoiceOf(current: AccountOnlyAuthSessionView | WorkspaceAuthSessionView): AccountChoiceView {
  const listed = currentChoiceOf(current);
  if (listed !== undefined) return listed;
  // The listing is re-read with the context and names every organization it was built from, so this
  // is a context the person can no longer see in it — shown by its own name, offering nothing.
  return {
    accountId: current.accountId,
    name: current.accountName,
    initials: current.accountName.slice(0, 2).toUpperCase(),
    state: current.accountState,
    accountRole: current.accountRole,
    workspace: [],
  };
}

/**
 * The next allowed destination for a context the person should not stay in (FR12-AUTH-12), with
 * the notice that says why — or `null` when the context is fine.
 */
export function nextAllowedMoveOf(
  current: AccountOnlyAuthSessionView | WorkspaceAuthSessionView,
  choice: AccountChoiceView,
  lastUsedWorkspaceId: string | null,
): { readonly target: { readonly accountId: string; readonly workspaceId: string | null }; readonly notice: ScopeNotice } | "blocked" | null {
  if (current.accountState === "suspended" && current.kind === "workspace_selected") {
    const target = defaultScopeTargetOf(choice, null);
    if (target === null) return "blocked";
    return { target, notice: { kind: "account_suspended", accountName: current.accountName } };
  }
  if (current.accountState === "suspended" && defaultScopeTargetOf(choice, null) === null) return "blocked";
  if (current.kind !== "workspace_selected") return null;
  const here = choice.workspace.find((one) => one.workspaceId === current.workspaceId);
  if (here === undefined || here.enterable) return null;
  const elsewhere: AccountChoiceView = {
    ...choice,
    workspace: choice.workspace.filter((one) => one.workspaceId !== current.workspaceId),
  };
  const target = defaultScopeTargetOf(elsewhere, lastUsedWorkspaceId);
  if (target === null) return "blocked";
  const destination = elsewhere.workspace.find((one) => one.workspaceId === target.workspaceId);
  return {
    target,
    notice: { kind: "workspace_archived", destinationName: destination === undefined ? choice.name : destination.name },
  };
}

interface ScopedFrameProps {
  readonly consoleVersion: string;
  readonly current: AccountOnlyAuthSessionView | WorkspaceAuthSessionView;
  /** When the auth_session was read — the impersonation countdown's clock (see the banner). */
  readonly fetchedAt: number;
}

function ScopedFrame({ consoleVersion, current, fetchedAt }: ScopedFrameProps): ReactNode {
  usePortalTranslation("shell");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const scope = useSwitchScope();
  const logOut = useLogOut();
  const narrow = useSyncExternalStore(subscribeToNarrow, isNarrow, () => false);
  const [collapsed, setCollapsed] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const workspaceAuthSession: WorkspaceAuthSessionView | null = current.kind === "workspace_selected" ? current : null;
  const inWorkspace = workspaceAuthSession !== null;
  const role = workspaceAuthSession === null ? null : workspaceAuthSession.memberRole;
  const navigationScope: NavigationScope = { memberRole: role, accountCapability: current.capability.account };
  const choice = currentAccountChoiceOf(current);
  const enterable = enterableWorkspaceOf(choice);
  const landing = landingPathOf(current) ?? "/";

  // Workspace reads — only with a workspace open: none of them means anything for the organization alone.
  const workspace = useQuery({ ...workspaceQuery(gateway), enabled: inWorkspace });
  const notification = useQuery({ ...notificationQuery(gateway), enabled: inWorkspace });
  const billing = useQuery({ ...billingQuery(gateway), enabled: inWorkspace });
  const currentWorkspace = useCurrentWorkspace();

  // The sidebar's count badges (the prototype's Agents 2 · Contact lists 3), read from the gateway
  // through the same query keys the pages use, so one read serves both. A count the role may not read
  // is never requested.
  const readsContactList = role !== null && memberCan(role, "contact_list.read");
  const voiceAgentList = useQuery({ ...voiceAgentListQuery(gateway), enabled: inWorkspace });
  const contactList = useQuery({
    ...contactListQuery(gateway),
    enabled: readsContactList,
    retry: false,
  });
  const count: NavigationCount = {
    voiceAgent: figureOf(voiceAgentList, inWorkspace, (data) => data.length),
    contactList: figureOf(contactList, readsContactList, (data) => data.listCount),
  };

  const readAll = useMutation({
    mutationFn: () => gateway.readAllNotifications(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.notification });
    },
  });

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const staticTitle = useRouterState({
    select: (state) => state.matches.at(-1)?.staticData.pageTitle ?? "wetalk",
  });
  const titleVoiceAgentId = useRouterState({
    select: (state) => {
      const last = state.matches.at(-1);
      if (last?.staticData.pageTitleIsVoiceAgentName !== true) return null;
      const params: Record<string, string> = last.params;
      return params["voiceAgentId"] ?? null;
    },
  });
  const titleVoiceAgent =
    titleVoiceAgentId === null
      ? undefined
      : voiceAgentList.data?.find((one) => one.voiceAgentId === titleVoiceAgentId);
  const pageTitle = titleVoiceAgent?.displayName ?? resolvePortalMessage(staticTitle);
  useEffect(() => { globalThis.document.title = `${pageTitle} · wetalk`; }, [pageTitle]);

  // Deep links re-check access (FR12-AUTH-10): a page this context may not show is never drawn.
  const allowed = pathAllowedIn(current, pathname);
  useEffect(() => {
    if (!allowed) void navigate({ to: landing, replace: true });
  }, [allowed, landing, navigate]);

  // The next allowed destination (FR12-AUTH-12), tried once per scope generation.
  const move = nextAllowedMoveOf(current, choice, lastUsedWorkspaceOf(current.person.personId, current.accountId));
  const moved = useRef<number | null>(null);
  useEffect(() => {
    if (move === null || move === "blocked" || moved.current === current.scopeGeneration) return;
    moved.current = current.scopeGeneration;
    scope.switchTo(move.target, move.notice);
  }, [move, current.scopeGeneration, scope]);

  // The prototype's one name for the button, "Menu"; whether the sidebar is shown is `aria-expanded` (design audit
  // 2026-10-15 — it used to say "Open menu", "Close menu", "Show menu" or "Collapse menu").
  const menuLabel = portalText("shell", "menu.label");
  const mayCreateWorkspace = current.capability.account.includes("account.workspace.create");

  return (
    <div className="c-shell" data-sidebar={collapsed ? "collapsed" : "shown"}>
      {/* Above the chrome, not inside it: if a WeTalk operator is acting as this person, that
          is the first thing they should see, before the sidebar or anything they might act on. */}
      <ImpersonationBanner
        actor={current.actor}
        subjectDisplayName={current.person.displayName}
        // The query's own fetch time, not `Date.now()`: calling an impure function during
        // render is unstable, and this is strictly better anyway. The countdown now advances
        // on exactly the tick that decides whether the banner exists at all, so the number and
        // the notice can never disagree about how live the grant is.
        nowMs={fetchedAt}
      />
      <Sidebar
        consoleVersion={consoleVersion}
        scope={navigationScope}
        homePath={landing}
        count={count}
        open={overlayOpen}
        onClose={() => {
          setOverlayOpen(false);
        }}
      />

      <div className="c-main">
        <header className="c-topbar">
          <IconButton
            glyph={GLYPH.listBars}
            label={menuLabel}
            title={menuLabel}
            size="xl"
            aria-controls="c-sidebar"
            aria-expanded={narrow ? overlayOpen : !collapsed}
            onClick={() => {
              if (narrow) setOverlayOpen((open) => !open);
              else setCollapsed((folded) => !folded);
            }}
          />

          <OrganizationPicker
            current={choice}
            choice={current.accountChoice}
            pending={scope.busy}
            impersonated={current.actor.kind === "operator"}
            settingsOpen={current.capability.account.includes("account.profile.read")}
            closed={<ClosedOrganizationMenu enabled={current.actor.kind === "self"} />}
            onPick={(picked) => {
              const target = targetOfPick(current.person.personId, picked);
              if (target !== null) scope.switchTo(target);
            }}
          />

          <span className="c-topbar-slash" aria-hidden="true">
            /
          </span>

          {/* With the organization alone open the picker is still drawn (owner, 2026-11-03: "it says no workspace open
              and i cannot select from dropdown"), so a workspace can be entered from the top bar. Only a finance-only
              person with no workspace to enter keeps the plain words. */}
          {workspaceAuthSession === null && current.landing === "finance" && enterable.length === 0 ? (
            <span className="c-topbar-no-workspace">{portalText("shell", "scope.financeOnly")}</span>
          ) : (
            <WorkspacePicker
              current={currentWorkspace.workspace}
              currentId={workspaceAuthSession === null ? null : workspaceAuthSession.workspaceId}
              problem={currentWorkspace.problem}
              onRetry={currentWorkspace.retry}
              enterable={enterable}
              figure={workspace.data ?? []}
              mayCreate={mayCreateWorkspace}
              onPick={(workspaceId) => {
                scope.switchTo({ accountId: current.accountId, workspaceId });
              }}
              pending={scope.busy}
            />
          )}

          <span className="c-topbar-crumb" aria-hidden="true">
            /
          </span>
          <span className="c-topbar-title">{pageTitle}</span>

          <div className="c-topbar-actions">
            <PortalLanguagePicker />
            {inWorkspace ? (
              <NotificationCentre
                notification={notification.data ?? []}
                onReadAll={() => {
                  readAll.mutate();
                }}
              />
            ) : null}

            <PersonMenu
              person={current.person}
              billing={billing.data}
              workspaceCount={workspace.data?.length ?? null}
              onLogOut={logOut}
              workspaceOpen={inWorkspace}
              personalSecurityOpen={current.actor.kind === "self"}
            />
          </div>
        </header>

        <SwitchStatus />
        <PostSwitchNotice />
        {/* Owner answer 47: a verified-domain offer to join another organization, asked — never applied. */}
        <DomainJoinOfferBanner personId={current.person.personId} enabled={current.actor.kind === "self"} />

        {/* The export of 2026-10-10: "{agent} is ready to test", above the billing alert, on every screen but Building and
            the agent's own page — in place of the toast the previous export drew bottom right. */}
        {inWorkspace ? <ReadyBanner /> : null}

        {inWorkspace && billing.data !== undefined ? (
          <BillingAlert
            billing={billing.data}
            onAddCredit={() => {
              void navigate({ to: "/billing" });
            }}
          />
        ) : null}

        {move === "blocked" ? (
          <BlockedScope current={current} />
        ) : allowed ? (
          <Outlet />
        ) : (
          <p className="c-note">{portalText("shell", "scope.opening", { name: current.accountName })}</p>
        )}
      </div>
    </div>
  );
}

/** A context with nowhere left to go inside it (a suspended organization the person cannot settle). */
function BlockedScope({ current }: { readonly current: AuthSessionView & { readonly accountName: string } }): ReactNode {
  usePortalTranslation("shell");
  return (
    <section className="c-page" aria-labelledby="c-blocked-scope-title">
      <h1 id="c-blocked-scope-title">{portalText("shell", "scope.blockedTitle", { name: current.accountName })}</h1>
      <p className="c-note c-note-critical">
        {portalText("shell", "scope.blocked")}
      </p>
    </section>
  );
}
